import { useState, useEffect } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  useDisclosure,
  Chip,
  Image,
} from '@heroui/react'

import {
  gifDisplayTitle,
  gifDisplayChips,
  gifScenarioExamples,
} from '../../gifs/types'
import type { Gif } from '../../gifs/types'
import { resolveGifUrl } from '../../gifs/services'

interface DocumentPreviewModalProps {
  gif: Gif
  children: React.ReactNode
  className?: string
}

interface MetadataSectionProps {
  label: string
  children: React.ReactNode
}

function MetadataSection({ label, children }: MetadataSectionProps) {
  return (
    <div>
      <span className="font-medium text-slate-600 dark:text-slate-400">
        {label}:
      </span>
      {children}
    </div>
  )
}

interface ChipListProps {
  items: string[]
  maxVisible: number
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'default'
}

function ChipList({ items, maxVisible, color }: ChipListProps) {
  const visibleItems = items.slice(0, maxVisible)
  const remainingCount = items.length - maxVisible

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {visibleItems.map((item, i) => (
        <Chip key={i} size="sm" variant="flat" color={color}>
          {item}
        </Chip>
      ))}
      {remainingCount > 0 && (
        <Chip size="sm" variant="flat" color="default">
          +{remainingCount}
        </Chip>
      )}
    </div>
  )
}

function SimpleGifImage({ gif }: { gif: Gif }) {
  const [resolvedUrl, setResolvedUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let isMounted = true

    const resolveUrl = async () => {
      try {
        setIsLoading(true)
        setHasError(false)
        const url = await resolveGifUrl(gif.gifUrl)
        if (isMounted) {
          setResolvedUrl(url)
          setIsLoading(false)
        }
      } catch (error) {
        console.error('Error resolving GIF URL:', error)
        if (isMounted) {
          setHasError(true)
          setIsLoading(false)
        }
      }
    }

    resolveUrl()
    return () => {
      isMounted = false
    }
  }, [gif.gifUrl])

  if (isLoading) {
    return (
      <div className="w-full h-full bg-slate-200 dark:bg-slate-700 animate-pulse flex items-center justify-center">
        <span className="text-slate-500 text-xs">Loading...</span>
      </div>
    )
  }

  if (hasError) {
    return (
      <div className="w-full h-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
        <div className="text-slate-500 text-xs text-center">
          <div>Error</div>
          <div className="mt-1">{gif.id.slice(-8)}</div>
        </div>
      </div>
    )
  }

  return (
    <Image
      src={resolvedUrl}
      alt={`GIF ${gif.id}`}
      className="max-w-full max-h-full object-contain"
      classNames={{
        wrapper: 'max-w-full max-h-full',
        img: 'max-w-full max-h-full object-contain',
      }}
    />
  )
}

export function DocumentPreviewModal({
  gif,
  children,
  className,
}: DocumentPreviewModalProps) {
  const { isOpen, onOpen, onOpenChange } = useDisclosure()

  return (
    <>
      <div
        className={`cursor-pointer ${className || ''}`}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onOpen()
          }
        }}
        role="button"
        tabIndex={0}
      >
        {children}
      </div>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="font-mono text-xs text-slate-500">
                  GIF Details: {gif.id}
                </div>
              </ModalHeader>
              <ModalBody className="pb-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* GIF Preview */}
                  <div className="space-y-4">
                    <div className="w-full max-h-80 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <SimpleGifImage gif={gif} />
                    </div>

                    {/* Basic Metadata */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="font-medium text-slate-600 dark:text-slate-400">
                          Duration:
                        </span>
                        <span className="ml-1 text-slate-700 dark:text-slate-300">
                          {gif.durationSeconds.toFixed(1)}s
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-slate-600 dark:text-slate-400">
                          Rating:
                        </span>
                        <Chip
                          size="sm"
                          variant="flat"
                          color="warning"
                          className="ml-1"
                        >
                          {gif.explicitRating}
                        </Chip>
                      </div>
                      <div>
                        <span className="font-medium text-slate-600 dark:text-slate-400">
                          Created:
                        </span>
                        <span className="ml-1 text-slate-700 dark:text-slate-300">
                          {gif.createdAt.toLocaleDateString()}
                        </span>
                      </div>
                      {gif.promptVersion && (
                        <div>
                          <span className="font-medium text-slate-600 dark:text-slate-400">
                            Version:
                          </span>
                          <span className="ml-1 text-slate-700 dark:text-slate-300">
                            {gif.promptVersion}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="space-y-4 text-sm">
                    {/* Reference Information */}
                    {(gif.reference?.source ||
                      gif.reference?.keywords?.length > 0 ||
                      gif.reference?.year) && (
                      <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                        <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Reference
                        </h4>
                        {gif.reference.source && (
                          <MetadataSection label="Source">
                            <span className="ml-2 text-slate-700 dark:text-slate-300">
                              {gif.reference.source}
                              {gif.reference.year && ` (${gif.reference.year})`}
                            </span>
                          </MetadataSection>
                        )}
                        {gif.reference.keywords?.length > 0 && (
                          <MetadataSection label="Keywords">
                            <ChipList
                              items={gif.reference.keywords}
                              maxVisible={3}
                              color="default"
                            />
                          </MetadataSection>
                        )}
                      </div>
                    )}

                    {/* Retrieval prose (narrative / tags / scenarios) */}
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                      <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Retrieval
                      </h4>

                      <MetadataSection label="Description">
                        <div className="text-slate-700 dark:text-slate-300 text-xs mt-1 leading-relaxed">
                          {gifDisplayTitle(gif)}
                        </div>
                      </MetadataSection>

                      {gif.retrievalSearch?.narrative && (
                        <MetadataSection label="Narrative">
                          <div className="text-slate-700 dark:text-slate-300 text-xs mt-1 leading-relaxed">
                            {gif.retrievalSearch.narrative}
                          </div>
                        </MetadataSection>
                      )}

                      {gifDisplayChips(gif).length > 0 && (
                        <MetadataSection label="Tags">
                          <ChipList
                            items={gifDisplayChips(gif)}
                            maxVisible={6}
                            color="primary"
                          />
                        </MetadataSection>
                      )}

                      {gifScenarioExamples(gif).length > 0 && (
                        <MetadataSection label="Scenarios">
                          <div className="space-y-1 mt-1">
                            {gifScenarioExamples(gif)
                              .slice(0, 4)
                              .map((scenario, index) => (
                                <p
                                  key={index}
                                  className="text-slate-700 dark:text-slate-300 text-xs"
                                >
                                  • {scenario}
                                </p>
                              ))}
                          </div>
                        </MetadataSection>
                      )}
                    </div>

                    {/* Popularity */}
                    {(gif.popularity?.copies > 0 ||
                      gif.popularity?.favorited > 0) && (
                      <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                        <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Popularity
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="font-medium text-slate-600 dark:text-slate-400">
                              Copies:
                            </span>
                            <span className="ml-1 text-slate-700 dark:text-slate-300">
                              {gif.popularity.copies}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-slate-600 dark:text-slate-400">
                              Favorited:
                            </span>
                            <span className="ml-1 text-slate-700 dark:text-slate-300">
                              {gif.popularity.favorited}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
